import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import moment from 'moment';
import {
  getAppointments,
  getClientAppointments,
} from 'src/redux/services/calenderServices';
import { isEmptyObject } from 'src/legacy/utils/helpers';
import AppointmentIcon from 'src/assets/icons/icon-appointments.svg';
import { TypographyVariant } from 'src/latest/src/types';
import { TimeZoneAbbreviated } from '../../utils';
import { HoldsPayload } from '../calendar/calendarTypes';
import AppointmentList from './AppointmentList';
import PopAheadSearch from '../../components/PopAheadSearch';
import NoData from '../../components/NoData';
import { SquareIcon } from '../../components';
import './Appointments.scss';
import { Typography } from 'src/latest/src/components';

type Props = {
  title: string;
  selectedUserId: string;
  selectedUserType: string[];
  appointmentDays?: number;
  titleVariant?: TypographyVariant;
  hideDatesOnSections?: boolean;
  showSearch?: boolean;
  showCalendar?: boolean;
  fromTherapistDashboard?: boolean;
};

let isInitialised = false;
const appointmentNoOfDays = 30;

export default function Appointments({
  title,
  selectedUserId,
  selectedUserType,
  appointmentDays,
  titleVariant,
  hideDatesOnSections,
  showSearch,
  showCalendar,
  fromTherapistDashboard = false,
}: Props) {
  const navigate = useNavigate();
  const { loggedInUser } = useSelector(
    ({ authentication }: any) => authentication,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [appointments, setAppointments] = useState<any>();
  const [activeTooltipId, setActiveTooltipId] = useState<string | undefined>();

  const handleToggleTooltip = (tooltipId: string) => {
    setActiveTooltipId(prevId =>
      prevId === tooltipId ? undefined : tooltipId,
    );
  };

  useEffect(() => {
    if (selectedUserType && !isInitialised) {
      fetchAppointments();
      isInitialised = true;
    }
  }, [selectedUserType]);

  useEffect(() => {
    return () => {
      isInitialised = false;
    };
  }, []);

  const fetchAppointments = async (name?: string) => {
    try {
      if (selectedUserType.includes('THERAPIST')) {
        await fetchTherapistAppointments(name);
      } else if (selectedUserType.includes('CLIENT')) {
        await fetchClientAppointments(name);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Failed to fetch appointments:', error);
    }
  };

  const getStartDate = (): string => {
    return new Date()
      .toISOString()
      .replace(/T(\d{2}:\d{2}:\d{2})/, 'T00:00:00');
  };

  const fetchTherapistAppointments = async (name?: string) => {
    setIsLoading(true);

    const req: HoldsPayload = {
      start_date: getStartDate(),
      for_days: appointmentDays || appointmentNoOfDays,
      therapist_ids: [selectedUserId],
      search_by_client_name: name,
    };

    const { data } = await getAppointments(req);

    setIsLoading(false);
    handleAppointmentsResponse(data);
  };

  const fetchClientAppointments = async (name?: string) => {
    setIsLoading(true);

    const req: HoldsPayload = {
      start_date: getStartDate(),
      for_days: appointmentDays || appointmentNoOfDays,
      client_ids: [selectedUserId],
      search_by_therapist_name: name,
    };

    const { data } = await getClientAppointments(req);

    setIsLoading(false);
    handleAppointmentsResponse(data);
  };

  const handleAppointmentsResponse = (scheduleData: any) => {
    const currentUserAppointmentsData = scheduleData
      ? scheduleData[selectedUserId]
      : undefined;

    if (currentUserAppointmentsData) {
      setAppointments(currentUserAppointmentsData.slot_data);
    } else {
      setAppointments(undefined);
    }
  };

  return (
    <div className="appointments-container">
      <div className="appointments-content">
        <div className="appointments-header">
          <div className="left-section d-flex flex-column justify-content-center">
            <div className="appointments-header-title">
              {fromTherapistDashboard && (
                <div>
                  <Typography variant="size-34" color="color-2">
                    Today's Appointments
                  </Typography>
                </div>
              )}
              <div>
                <Typography variant={titleVariant || 'size-34'} color="color-2">
                  {title || 'Appointments'}
                </Typography>
                <Typography
                  variant="size-12"
                  color="color-28"
                  className="weight-semi-bold"
                >
                  All times in {TimeZoneAbbreviated(new Date().toISOString())}
                </Typography>
              </div>
            </div>
          </div>

          <div className="right-section">
            {showSearch && (
              <PopAheadSearch
                id="appointment-search"
                onSearch={(name: string) => {
                  if (!name) {
                    fetchAppointments();
                  }

                  if (name.length > 2) {
                    fetchAppointments(name);
                  }
                }}
              />
            )}
            {showCalendar && (
              <SquareIcon
                src={AppointmentIcon}
                onClick={() => {
                  navigate(`/profile/${loggedInUser.id}/#appointments`);
                }}
              />
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="th-parcticeprofile-loader">
            <Spinner animation="border" role="status" />
          </div>
        ) : (
          <div className="appointments-list mt-5">
            {appointments && !isEmptyObject(appointments) ? (
              <>
                {Object.keys(appointments).map(date => (
                  <div key={date} className="mt-3">
                    <AppointmentList
                      title={
                        hideDatesOnSections
                          ? ''
                          : moment(date).format('dddd, MMMM Do')
                      }
                      appointmentsData={appointments[date]}
                      onDelete={(appointmentId: number) =>
                        fetchAppointments(appointmentId.toString())
                      }
                      selectedUserType={selectedUserType}
                      activeToolTipId={activeTooltipId}
                      setActiveToolTip={handleToggleTooltip}
                    />
                  </div>
                ))}
              </>
            ) : (
              <div className="mt-3">
                <NoData
                  message="No appointments found"
                  className="appointment-width"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
